<script setup lang="ts">
/**
 * OTP settings: whether signing in to this company's account asks for the
 * 6-digit code Xobo emails after the password. On by default; a company
 * admin can turn it off here (for example when codes are not reaching their
 * team), and Xobo staff can change it from Mission Control.
 *
 * GET / PATCH /account/settings, company from the token. The server lets a
 * customer change login_email_otp and nothing else, and only an admin
 * (router ADMIN_ONLY_PATH_PREFIXES keeps members off this page too).
 * Turning the code off asks for confirmation in place; turning it on saves
 * straight away. It applies from the next sign-in.
 */
import { onMounted, ref } from "vue";
import axios from "axios";
import { authHeaders, reasonFrom } from "../../composables/useCompanyKyc";

const enabled = ref<boolean | null>(null);
const loading = ref(true);
const saving = ref(false);
const error = ref("");
const saveError = ref("");
const saved = ref("");
const confirmingOff = ref(false);
// False when the server predates the setting: the page says so instead of
// offering a switch that cannot save.
const supported = ref(true);

const settingsUrl = () => `${import.meta.env.VITE_ONBOARDING_SERVICE}account/settings`;

onMounted(async () => {
  try {
    const res = await axios.get(settingsUrl(), { headers: authHeaders() });
    const value = res.data?.data?.login_email_otp;
    supported.value = typeof value === "boolean";
    enabled.value = value !== false;
  } catch (err) {
    error.value = reasonFrom(err, "Could not load your OTP settings.");
  } finally {
    loading.value = false;
  }
});

async function save(next: boolean) {
  saving.value = true;
  saveError.value = "";
  saved.value = "";
  try {
    const res = await axios.patch(settingsUrl(), { login_email_otp: next }, { headers: authHeaders() });
    enabled.value = res.data?.data?.login_email_otp !== false;
    confirmingOff.value = false;
    saved.value = enabled.value
      ? "Saved. Signing in will ask for the emailed code."
      : "Saved. Signing in will no longer ask for a code.";
  } catch (err) {
    saveError.value = reasonFrom(err, "Could not save. Please try again.");
  } finally {
    saving.value = false;
  }
}

function toggle() {
  if (saving.value || enabled.value === null) return;
  saved.value = "";
  saveError.value = "";
  if (enabled.value) confirmingOff.value = true;
  else void save(true);
}
</script>

<template>
  <div class="ac-card" v-loading="loading">
    <div class="ac-panel-head">
      <div>
        <h2 class="ac-panel-title">OTP settings</h2>
        <p class="ac-panel-desc">The one-time code asked for when someone signs in</p>
      </div>
    </div>

    <div class="ac-card-body">
      <div v-if="error" class="ac-error">{{ error }}</div>

      <p v-else-if="!supported" class="ac-note">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
        <span>Signing in asks for a code we email to you. Turning it off from here isn't available yet; contact Xobo support if codes aren't reaching your team.</span>
      </p>

      <template v-else-if="enabled !== null">
        <div class="ac-section-label">Sign-in</div>
        <div class="otp-row">
          <div class="otp-row__text">
            <div id="otp-label" class="otp-row__title">Email a code at sign-in</div>
            <p class="otp-row__desc">
              After their password, everyone on your account enters a 6-digit code we email to them.
              If codes aren't reaching your team, you can turn this off.
            </p>
          </div>
          <div class="otp-control">
            <span class="ac-pill" :class="saving ? 'ac-pill--info' : enabled ? 'ac-pill--ok' : 'ac-pill--muted'" aria-live="polite">
              {{ saving ? "Saving…" : enabled ? "On" : "Off" }}
            </span>
            <button
              id="otp-switch"
              type="button"
              role="switch"
              class="otp-switch"
              :class="{ 'is-on': enabled }"
              :aria-checked="enabled ? 'true' : 'false'"
              aria-labelledby="otp-label"
              :disabled="saving || confirmingOff"
              @click="toggle"
            >
              <span class="otp-switch__thumb" />
            </button>
          </div>
        </div>

        <div v-if="confirmingOff" class="otp-confirm" role="alertdialog" aria-labelledby="otp-confirm-title">
          <p id="otp-confirm-title" class="otp-confirm__title">Turn off sign-in codes?</p>
          <p class="otp-confirm__body">
            Anyone with a team member's email and password will be able to sign in without the emailed code.
            This applies to everyone on your account.
          </p>
          <div class="otp-confirm__actions">
            <button type="button" class="ac-btn ac-btn--sm" :disabled="saving" @click="save(false)">
              {{ saving ? "Turning off…" : "Turn off codes" }}
            </button>
            <button type="button" class="ac-btn ac-btn--ghost ac-btn--sm" :disabled="saving" @click="confirmingOff = false">
              Keep them on
            </button>
          </div>
        </div>

        <p v-if="saveError" class="ac-error otp-msg">{{ saveError }}</p>
        <p v-else-if="saved" class="otp-saved otp-msg" role="status">{{ saved }}</p>

        <div class="ac-actions">
          <p class="ac-note">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
            <span>Changes apply the next time someone signs in. Anyone already signed in stays signed in.</span>
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.otp-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 4px 0 22px;
}
.otp-row__text {
  min-width: 0;
  max-width: 60ch;
}
.otp-row__title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #12284a;
}
.otp-row__desc {
  margin: 4px 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #64748b;
}
.otp-control {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* Switch, drawn to match the portal's navy controls. */
.otp-switch {
  position: relative;
  width: 44px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: #cbd5e1;
  cursor: pointer;
  transition: background 0.15s;
}
.otp-switch.is-on {
  background: #12284a;
}
.otp-switch:focus-visible {
  outline: 2px solid #12284a;
  outline-offset: 2px;
}
.otp-switch:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.otp-switch__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.25);
  transition: transform 0.15s;
}
.otp-switch.is-on .otp-switch__thumb {
  transform: translateX(20px);
}
@media (prefers-reduced-motion: reduce) {
  .otp-switch,
  .otp-switch__thumb {
    transition: none;
  }
}

.otp-confirm {
  margin: 0 0 18px;
  padding: 14px 16px;
  border: 1.5px solid #fde68a;
  border-radius: 10px;
  background: #fffbeb;
}
.otp-confirm__title {
  margin: 0;
  font-size: 0.86rem;
  font-weight: 600;
  color: #92400e;
}
.otp-confirm__body {
  margin: 4px 0 12px;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #78350f;
  max-width: 62ch;
}
.otp-confirm__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.otp-msg {
  margin: 0 0 18px;
}
.otp-saved {
  font-size: 0.8rem;
  font-weight: 500;
  color: #15803d;
}

@media (max-width: 640px) {
  .otp-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
